// 原系統「查詢區塊」的篩選欄位 → 畫面畫得出來的那一部分。
//
// 與 legacy-search-forms.generated.ts 的分工同前三組：那邊是**資料**（由
// scripts/extract-legacy-search-forms.ts 從 raw-dom 產生，不手改），這邊是**規則**。
//
// ---------------------------------------------------------------------------
// 解決的問題：查詢區只有一個「關鍵字」欄位
// ---------------------------------------------------------------------------
// 原系統每一頁的查詢區有 3～13 個篩選欄位（學年度／醫院／訓練類別／科部／狀態…），
// React 的 CrudListPage 與 ReportPage 一律只畫一個關鍵字欄位
// （CrudListPage.tsx 的 search-field-keyword、ReportPage.tsx 同）。
// 於是原系統那一整排 label 與控制項在 structure 比對裡全部落成「缺元素」。
//
// ---------------------------------------------------------------------------
// 閘門：只畫「React 能逐位元組重現的控制項」（沿用 extract-legacy-tables 的原則）
// ---------------------------------------------------------------------------
// extract-legacy-tables.ts 的檔頭寫過：「形狀資料只要有一頁是錯的，拿它去排版會排出比
// 原本更糟的結果」。這裡有一個更尖銳的版本：**把 1 筆「缺元素」補成半套，會變成 N 筆
// 新差異，總筆數不減反增**。
//
// 具體機制（用 announcement-announcement-list 的 `<select name="type">` 說明）：
//   原系統該 select 底下有 7 個 option，比對時整棵算 **1 筆**「缺元素」。
//   如果 React 畫一個「空的 `<select name="type">`」，select 本身會配對成功，
//   於是 7 個 option 變成 **7 筆**「缺元素」—— 1 筆換 7 筆。
// 所以閘門是：
//   ① 控制項要嘛能**完整**重現（標籤 + 全部 allowlist 屬性 + 全部子節點），要嘛不畫。
//   ② 一組欄位裡不能重現的控制項就跳過，但那一組的 **label 照畫**——
//      label 是純靜態文字，能逐字重現，畫它只會少一筆、不會多一筆。
//
// 依 ① 被擋下來的實際內容（數字由 searchFormStats() 現算，不寫死）：
//   - 有 >=1 個 option 但 option 不是固定列舉的 `<select>`（外鍵下拉，選項是資料庫內容）
//   - 帶 value 屬性的 text/number input（原系統的預設日期，擷取當下才有的值）
// 這兩類**刻意不畫**：畫了會讓「缺元素」變成「屬性不同／option 缺元素」，
// 筆數不會少，只是換一個類別，看起來像有進展而已。

import {
  LEGACY_SEARCH_FORMS,
  type LegacySearchControl,
  type LegacySearchForm,
  type LegacySearchGroup,
} from '@/mocks/legacy-search-forms.generated';

export type {
  LegacySearchControl,
  LegacySearchForm,
  LegacySearchGroup,
  LegacySearchOption,
} from '@/mocks/legacy-search-forms.generated';

/**
 * 能用原生 `<input>` 直接畫、且不需要任何值的 type。
 * 這些 type 的 React 端輸出（type/name/maxlength/readonly/required/placeholder）
 * 與原系統 allowlist 屬性一一對應。
 */
export const RENDERABLE_INPUT_TYPES: ReadonlySet<string> = new Set([
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
  'date',
  'file',
  'checkbox',
  'radio',
]);

/**
 * 這個控制項能不能**完整**重現？
 *
 * - `text`／`choice`：純靜態文字與送出代碼，能。
 * - `input`：type 在 RENDERABLE_INPUT_TYPES 內，且原系統**沒有** value 屬性。
 *   有 value 的（實測全站查詢表單 14 個，其中 12 個是擷取當下的日期）不畫：
 *   照抄會把擷取時刻的資料寫死進雛形，不照抄則變成一筆「屬性不同」——兩條路都不對。
 * - `select`：option 數為 0（原系統本來就是空的，執行期才 AJAX 填），
 *   或 option 通過抽取器的 optionsAreEnum()（固定列舉，已抽下來）。
 *   其餘一律不畫，理由見檔頭「1 筆換 7 筆」。
 * - `textarea`：原系統查詢表單實測 0 個，規則保留但沒有資料走到這裡。
 */
export function isRenderableControl(control: LegacySearchControl): boolean {
  switch (control.kind) {
    case 'text':
    case 'choice':
      return true;
    case 'input':
      return (
        RENDERABLE_INPUT_TYPES.has(control.type ?? 'text') &&
        control.hasUnreproducibleValue !== true
      );
    case 'select':
      return control.options !== undefined || control.optionCount === 0;
    case 'textarea':
      return true;
    default:
      return false;
  }
}

/** 一組欄位裡畫得出來的控制項（順序不變）。 */
export function renderableControlsOf(
  group: LegacySearchGroup,
): readonly LegacySearchControl[] {
  return group.controls.filter(isRenderableControl);
}

/**
 * 這一組要不要畫：label 非空，或至少有一個畫得出來的控制項。
 * 兩者都沒有的組完全跳過（畫出來只會是一個空白列）。
 */
export function isRenderableGroup(group: LegacySearchGroup): boolean {
  return group.label !== '' || renderableControlsOf(group).length > 0;
}

/**
 * pageId → 這一頁查詢區要畫的欄位組；沒有實測資料、或沒有任何一組通過閘門時 undefined
 * （呼叫端維持原本「只有一個關鍵字欄位」的行為）。
 *
 * ⚠ 只有使用 SearchPanel 的頁型（CrudListPage／ReportPage）會呼叫這支。
 * 抽取器對 78 頁抽到欄位，其中 20 頁是 display／complex-form 頁型，那兩個模板沒有查詢區，
 * 不在本規則的射程內。
 */
export function legacySearchGroupsFor(pageId: string): readonly LegacySearchGroup[] | undefined {
  const form: LegacySearchForm | undefined = LEGACY_SEARCH_FORMS[pageId];
  if (form === undefined) return undefined;
  const groups = form.groups.filter(isRenderableGroup);
  return groups.length === 0 ? undefined : groups;
}

/**
 * W8（批次 42，issue #40）：查詢 form 契約中某欄 input 的 maxLength／placeholder。
 *
 * 背景：LegacyFieldControl（legacy-fields.generated，渲染資料源）**沒有**這兩個屬性
 * （recon-A #58：account-v-2 keyword 在 legacy-fields.generated.ts:73 無此屬性），
 * 但查詢 form 契約（legacy-search-forms.generated，同一份 raw DOM 的另一條抽取路徑）有
 * ——例：`{ kind:'input', name:'keyword', type:'text', maxLength:20,
 * placeholder:'可為姓名、員編或學號' }`（generated :140；accountV2.jsp:663）。
 * 渲染端（LegacyContractFields）用本函式反查補帶；未命中（非 input／無屬性）回 undefined。
 * 全站交集（W8 實作時盤點）＝40+ 頁 99 處（maxLength 10 的日期欄群＋placeholder 的
 * searchStudentCode 群），屬性差正向收斂。
 */
export function searchInputAttributesFor(
  pageId: string,
  name: string,
): { maxLength?: number; placeholder?: string; readOnly?: boolean } | undefined {
  const form: LegacySearchForm | undefined = LEGACY_SEARCH_FORMS[pageId];
  if (form === undefined) return undefined;
  for (const group of form.groups) {
    for (const control of group.controls) {
      if (control.kind !== 'input' || control.name !== name) continue;
      const attrs: { maxLength?: number; placeholder?: string; readOnly?: boolean } = {};
      if (control.maxLength !== undefined) attrs.maxLength = control.maxLength;
      if (control.placeholder !== undefined) attrs.placeholder = control.placeholder;
      if (control.readOnly === true) attrs.readOnly = true;
      return Object.keys(attrs).length === 0 ? undefined : attrs;
    }
  }
  return undefined;
}

export interface SearchFormStats {
  /** 有實測查詢表單資料的頁數。 */
  readonly pages: number;
  /** 全部欄位組數 / 通過閘門的組數。 */
  readonly groups: number;
  readonly renderableGroups: number;
  /** 全部控制項數 / 通過閘門的控制項數。 */
  readonly controls: number;
  readonly renderableControls: number;
  /** 被閘門擋掉的控制項，依原因分類。 */
  readonly blocked: Readonly<Record<string, number>>;
}

/**
 * 閘門攤提：擋掉幾個、為什麼。現算不寫死 —— 材料換一份數字就會跟著換。
 * 單元測試用它把「閘門被改鬆」變成紅燈。
 */
export function searchFormStats(): SearchFormStats {
  const blocked: Record<string, number> = {
    'select：外鍵下拉，選項是資料庫內容': 0,
    'input：原系統帶 value（擷取當下的預設值）': 0,
    'input：type 不在 RENDERABLE_INPUT_TYPES': 0,
    其他: 0,
  };
  let groups = 0;
  let renderableGroups = 0;
  let controls = 0;
  let renderableControls = 0;
  for (const form of Object.values(LEGACY_SEARCH_FORMS)) {
    for (const group of form.groups) {
      groups += 1;
      if (isRenderableGroup(group)) renderableGroups += 1;
      for (const control of group.controls) {
        controls += 1;
        if (isRenderableControl(control)) {
          renderableControls += 1;
          continue;
        }
        if (control.kind === 'select') {
          blocked['select：外鍵下拉，選項是資料庫內容'] =
            (blocked['select：外鍵下拉，選項是資料庫內容'] ?? 0) + 1;
        } else if (control.kind === 'input' && control.hasUnreproducibleValue === true) {
          blocked['input：原系統帶 value（擷取當下的預設值）'] =
            (blocked['input：原系統帶 value（擷取當下的預設值）'] ?? 0) + 1;
        } else if (control.kind === 'input') {
          blocked['input：type 不在 RENDERABLE_INPUT_TYPES'] =
            (blocked['input：type 不在 RENDERABLE_INPUT_TYPES'] ?? 0) + 1;
        } else {
          blocked.其他 = (blocked.其他 ?? 0) + 1;
        }
      }
    }
  }
  return {
    pages: Object.keys(LEGACY_SEARCH_FORMS).length,
    groups,
    renderableGroups,
    controls,
    renderableControls,
    blocked,
  };
}
